// Source attribution capture for feature #7.
//
// On app boot, snapshots URL params + document.referrer + landing URL into
// sessionStorage. Persists across in-app navigation (sessionStorage scope =
// browser tab). First call wins — re-navigation within the app doesn't
// clobber the original landing context.
//
// Usage:
//   import { captureUtm, getCapturedUtm } from "./utils/captureUtm";
//   captureUtm();                  // once, on app boot
//   const utm = getCapturedUtm();  // before any candidate-create network call

const STORAGE_KEY = "ample_utm";

const safeGet = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const safeSet = (obj) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
  } catch {
    // storage disabled (Safari private mode) — silently drop
  }
};

/**
 * Snapshot the current page's UTM/referrer/landing into sessionStorage.
 * Idempotent — first call wins so an SPA navigation doesn't overwrite the
 * actual acquisition context with internal links.
 */
export function captureUtm() {
  if (typeof window === "undefined") return null;
  const existing = safeGet();
  if (existing) return existing;

  let utm = {};
  try {
    const params = new URLSearchParams(window.location.search || "");
    const get = (k) => params.get(k) || undefined;
    utm = {
      utmSource: get("utm_source"),
      utmMedium: get("utm_medium"),
      utmCampaign: get("utm_campaign"),
      utmTerm: get("utm_term"),
      utmContent: get("utm_content"),
      referrerUrl: document.referrer || undefined,
      landingUrl: window.location.href,
    };
  } catch {
    utm = { landingUrl: window?.location?.href };
  }

  // Drop empty keys so the body sent later is clean.
  Object.keys(utm).forEach((k) => {
    if (utm[k] == null || utm[k] === "") delete utm[k];
  });

  safeSet(utm);
  return utm;
}

/** Returns the captured UTM payload, or {} if none. Safe to spread into bodies. */
export function getCapturedUtm() {
  return safeGet() || {};
}

/** Clear after a successful submission so repeat applies don't double-attribute. */
export function clearCapturedUtm() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
